import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Box, Code, Collapse, Flex, Group, Image, Title, UnstyledButton } from "@mantine/core";
import { useDisclosure, useLocalStorage } from "@mantine/hooks";
import { IconChevronDown, IconChevronRight, IconPhoto, IconTool } from "@tabler/icons-react";
import { memo } from "react";
import { ChatAttachment } from "./ChatAttachment.js";
const ToolResultBlockDisplay = ({ block }) => {
    const [opened, { toggle }] = useDisclosure(false);
    return (_jsxs(Flex, { direction: "column", gap: 4, children: [_jsx(UnstyledButton, { onClick: toggle, children: _jsxs(Flex, { align: "center", gap: 6, children: [opened ? _jsx(IconChevronDown, { size: 14 }) : _jsx(IconChevronRight, { size: 14 }), _jsx(IconTool, { size: 14 }), _jsx(Badge, { size: "sm", variant: "light", color: block.is_error ? "red" : "teal", children: "tool_result" }), _jsx(Title, { order: 5, size: "xs", fw: 600, children: block.tool_name })] }) }), _jsx(Collapse, { expanded: opened, children: _jsx(Code, { block: true, style: { overflow: "auto" }, w: 400, maw: 600, mt: 4, children: typeof block.content === "string" ? block.content : JSON.stringify(block.content, null, 2) }) })] }));
};
function getImageSrc(block) {
    const { source } = block;
    if (source.type === "base64") {
        return `data:${source.media_type};base64,${source.data}`;
    }
    return source.url;
}
const ImageBlockDisplay = ({ block }) => {
    const [opened, { toggle }] = useDisclosure(false);
    return (_jsxs(Flex, { direction: "column", gap: 4, children: [_jsx(UnstyledButton, { onClick: toggle, children: _jsxs(Flex, { align: "center", gap: 6, children: [opened ? _jsx(IconChevronDown, { size: 14 }) : _jsx(IconChevronRight, { size: 14 }), _jsx(IconPhoto, { size: 14 }), _jsx(Badge, { size: "sm", variant: "light", color: "blue", children: "image" })] }) }), _jsx(Collapse, { expanded: opened, children: _jsx(Image, { src: getImageSrc(block), alt: "User uploaded image", width: 400, height: 400, mt: 4 }) })] }));
};
const TextBlockDisplay = ({ block }) => {
    return (_jsx(Flex, { direction: "column", children: block.text }));
};
export const UserMessage = memo(({ content, attachments = [], messageId }) => {
    const [showToolUse] = useLocalStorage({
        key: "mapka-showToolUse",
        defaultValue: false,
        getInitialValueInEffect: false,
    });
    let contentToRender = content;
    if (!showToolUse) {
        contentToRender = content.filter(c => c.type === "text" || c.type === "image");
    }
    const hasContent = contentToRender.length > 0;
    const hasAttachments = attachments.length > 0;
    if (!hasContent && !hasAttachments) {
        return null;
    }
    return (_jsx(Group, { justify: "right", m: "xs", "data-list-item": true, children: _jsxs(Flex, { direction: "column", gap: 4, maw: "85%", "data-message-id": messageId, align: "flex-end", children: [hasAttachments && (_jsx(Group, { gap: "xs", justify: "right", children: attachments.map(a => (_jsx(ChatAttachment, { attachment: a }, a.id))) })), hasContent && (_jsx(Box, { bg: "light-dark(#fff4dd, #3d3520)", p: "xs", maw: "100%", style: {
                        borderRadius: "12px 12px 0 12px",
                        fontFamily: "monospace",
                        overflowWrap: "anywhere",
                    }, children: _jsx(Flex, { direction: "column", gap: 8, children: contentToRender.map((block, i) => {
                            const { type } = block;
                            switch (type) {
                                case "text":
                                    return _jsx(TextBlockDisplay, { block: block }, i);
                                case "tool_result":
                                    return _jsx(ToolResultBlockDisplay, { block: block }, i);
                                case "image":
                                    return _jsx(ImageBlockDisplay, { block: block }, i);
                                default:
                                    window.logger.error({ type }, "Unknown block type in user message");
                                    return null;
                            }
                        }) }) }))] }) }));
});
UserMessage.displayName = "UserMessage";
